import { createSlice, createAsyncThunk, PayloadAction, SliceCaseReducers, Draft, AsyncThunk } from '@reduxjs/toolkit'
import { Args, State } from 'app/types/redux-types'
import { StateStatus } from '../utils/client-constants'

/* TODO this is type for thunk api that will carry dispath and states to reducers and async fucntions
  need to find a way to infer the actual type along with fixing circular dependency. */
export type ThunkAPI = {
  dispatch: any
  state: any
  extra?: any
  rejectValue?: string
  fulfillWithValue?: unknown
  getState: () => any
  requestId: string
  signal: AbortSignal
}

export function createReduxSlice<T, AsyncThunks extends Record<string, (arg?: Args, thunkAPI?: any) => Promise<T>>>({
  name,
  initialState,
  asyncThunks,
  reducers = {}
}: {
  name: string
  initialState: State<T>
  asyncThunks?: AsyncThunks
  reducers?: SliceCaseReducers<State<T>>
}) {
  type StateType = State<T>
  const createdThunks = {} as {
    [K in keyof AsyncThunks]: AsyncThunk<T, Parameters<AsyncThunks[K]>[0], ThunkAPI>
  }

  if (asyncThunks) {
    ;(Object.entries(asyncThunks) as [keyof AsyncThunks, AsyncThunks[keyof AsyncThunks]][]).forEach(
      ([thunkName, asyncFn]) => {
        createdThunks[thunkName] = createAsyncThunk<T, Parameters<typeof asyncFn>[0], ThunkAPI>(
          `${name}/${thunkName.toString()}`,
          async (arg, thunkAPI) => await asyncFn(arg, thunkAPI)
        )
      }
    )
  }

  const slice = createSlice({
    name,
    initialState: initialState as StateType,
    reducers: {
      reset: () => initialState,
      setData: (state: Draft<StateType>, action: PayloadAction<T>) => {
        state.data = action.payload as Draft<T> | undefined
      },
      setError: (state: Draft<StateType>, action: PayloadAction<string>) => {
        state.error = action.payload
        state.status = StateStatus.FAILED
      },
      ...reducers
    },
    extraReducers: builder => {
      Object.values(createdThunks).forEach(thunk => {
        builder
          .addCase(thunk.pending, (state: Draft<StateType>) => {
            state.status = StateStatus.LOADING
            state.error = null
          })
          .addCase(thunk.fulfilled, (state: Draft<StateType>, action: PayloadAction<T>) => {
            state.status = StateStatus.SUCCEEDED
            state.data = action.payload as Draft<T> | undefined
          })
          .addCase(thunk.rejected, (state: Draft<StateType>, action) => {
            state.status = StateStatus.FAILED
            const { name: errorName, message, status, type, stack } = (action.payload as any) || {}
            state.error = action.payload
              ? `${errorName}: ${message}`
              : action.error?.message || 'Unknown error'
            state.errorDetails = action.payload ? { code: status ?? type, stack } : undefined
          })
      })
    }
  })

  return {
    ...slice,
    actions: {
      ...slice.actions,
      ...createdThunks
    } as typeof slice.actions & typeof createdThunks
  }
}
